/* eslint-disable max-len */
import React, { useEffect, useRef, useState } from 'react';
import { useObserverReturningStore } from '../../CodesThatsGottaGoSomewhereElse';
import { LFC } from '../../Legacy';
import { BoardType } from '../../api/ApiTypes';
import { UseLocationScope } from '../AppLocation';
import { useResolve } from '../Container';
import { PassportModel } from '../PassportModel';
import { useDashboardVersion } from '../account/AccountHooks';
import { BoardLimitHeaderBlock as BoardLimitHeaderView, PlanStateHeaderBlock } from '../account/PlanStateHeaderBlock';
import { NavigationView } from '../account/navigation/NavigationView';
import { NotificationsButton } from '../common/NotificationsButton';
import { Page, PageProps } from '../common/Page';
import { ShowIfHasPermission } from '../common/PermissionElements';
import { getReturnLocation } from '../common/ReturnLocation';
import { UpdatesButton } from '../common/UpdatesButton';
import { HelpButton } from '../help/HelpButton';
import { XmasHangingDecal } from '../occasions/xmas/XmasHangingDecal';
import { XmasLightsEditor } from '../occasions/xmas/XmasLightsEditor';
import { XmasSnow } from '../occasions/xmas/XmasSnow';
import { XmasTrigger } from '../occasions/xmas/XmasTrigger';
import { AlertLayer } from '../packages/alerts/AlertLayer';
import { HandLayer } from '../packages/hand/HandLayer';
import { HatLayer } from '../packages/hats/HatLayer';
import { LowerPresentLayer } from '../packages/presentation/LowerPresentLayer';
import { UpperPresentLayer } from '../packages/presentation/UpperPresentLayer';
import { ToastLayer } from '../packages/toast/ToastLayer';
import { WaitingRoomHeaderBlock } from '../packages/waitingRoom/WaitingRoomHeaderBlock';
import { PreferencesButton } from '../preferences/PreferencesButton';
import { AnonymousHeaderBlock } from './AnonymousHeaderBlock';
import { BoardInfo } from './BoardInfo';
import { ConfettiLayer } from './ConfettiLayer';
import { EditorCrumbsWithMenu } from './EditorCrumbsWithMenu';
import { EditorLoading } from './EditorLoading';
import { EditorPalette } from './EditorPalette';
import { EditorParams } from './EditorParams';
import { EditorScope } from './EditorScope';
import { useEditorSessionObserver } from './EditorSession';
import { EditorTelemetryContext } from './EditorTelemetryContext';
import { PointerHandler } from './PointerHandler';
import { TemplateHeaderBlock } from './TemplateHeaderBlock';
import { DeveloperView } from './developer/DeveloperView';
import { OverlayLayer } from './overlay/OverlayLayer';
import { ShareHeaderBlock } from './participants/ShareButton';
import { CaptureWheel, ToolLayer } from './tooling/ToolLayer';
import { TransformLayer } from './tooling/transform/TransformLayer';
import { CanvasView } from './ui/CanvasView';
import { HtmlRenderer } from './visualization/HtmlRenderer';
import { MeetingHeaderView } from './workflow/MeetingHeaderView';
import { DropSupport } from './workspace/DropSupport';
import { PasteSupport } from './workspace/PasteSupport';
import { AdapterLayer } from './workspace/adapters/AdapterLayer';
import { PawnLayer } from './workspace/participants/PawnLayer';
import { WidgetLayer } from './workspace/widgets/WidgetLayer';

const EditorLayout: LFC<EditorParams['options']> = ({ ui }) => {

    const version = useDashboardVersion();
    const rootElmt = useRef<any>();
    const setTicker = useState(0)[1];
    const boardInfo = useEditorSessionObserver(BoardInfo);
    const passport = useObserverReturningStore(useResolve(PassportModel));

    useEffect(() => setTicker(x => x + 1), [rootElmt, setTicker]);

    const notTemplate = boardInfo.type !== BoardType.template;
    const pageProps: PageProps = {
        canton: ui ? getReturnLocation() : null,
        navigation: version !== 'next' ? (ui && passport.verify('da:read') ? NavigationView : null) : null,
        leftPolicy: ['board', false, 20],
    };

    return <Page {...pageProps}>
        <ShowIfHasPermission requires="da:read">
            {boardInfo.workspaceId && <UseLocationScope name="workspace" value={boardInfo.workspaceId} />}
            {boardInfo.folderId && <UseLocationScope name="folder" value={boardInfo.folderId} />}
        </ShowIfHasPermission>
        <UseLocationScope name="board" value={boardInfo.id} label={boardInfo.name} />
        {ui && <Page.Header divide="hover">
            <EditorCrumbsWithMenu />
            <Page.HeaderCenter>
                <MeetingHeaderView />
                <BoardLimitHeaderView />
            </Page.HeaderCenter>
            <Page.HeaderPush>
                {notTemplate ? <>
                    <PlanStateHeaderBlock />
                    <WaitingRoomHeaderBlock />
                    <AnonymousHeaderBlock />
                    <ShareHeaderBlock />
                </> : <>
                    <TemplateHeaderBlock />
                </>}
                <ShowIfHasPermission requires="bo:write">
                    <Page.HeaderBlock data-tour="header-help">
                        <UpdatesButton />
                        <ShowIfHasPermission requires="da:read">
                            <NotificationsButton />
                        </ShowIfHasPermission>
                        <PreferencesButton />
                        <HelpButton />
                    </Page.HeaderBlock>
                </ShowIfHasPermission>
            </Page.HeaderPush>
        </Page.Header>}
        <Page.Content flush>
            <CanvasView>
                <HtmlRenderer />
                <PointerHandler />
                <CaptureWheel>
                    <ToolLayer />
                </CaptureWheel>
                <OverlayLayer />
                {ui && <>
                    <XmasLightsEditor />
                    <XmasHangingDecal />
                    <XmasSnow />
                    <XmasTrigger />
                    <ConfettiLayer />
                    <PawnLayer />
                    <HatLayer />
                    {/* <CallLayer /> */}
                    <LowerPresentLayer />
                    <TransformLayer />
                    {/* <SelectionTransformLayer /> */}
                    <CaptureWheel>
                        <AdapterLayer />
                        <WidgetLayer />
                    </CaptureWheel>
                    <AlertLayer />
                    <ToastLayer />
                    <HandLayer />
                </>}
                <PasteSupport />
                <DeveloperView />
                {/* <HelpSupport /> */}
            </CanvasView>
            <EditorPalette />
        </Page.Content>
        <DropSupport />
        <UpperPresentLayer />
    </Page>;
};

export const Editor: LFC<{ params: EditorParams }> = ({ params }) =>
    <EditorScope loader={() => <EditorLoading message="Shredding confetti..." progress={1} />} params={params}>
        <EditorTelemetryContext>
            <EditorLayout {...params.options} />
        </EditorTelemetryContext>
    </EditorScope>;
